import ProductFruitsCoordinatorClass from './coordinator';
import { isProductFruitsEnabled } from './config';

let coordinatorPromise = null;
let unloadHookRegistered = false;
let bootstrapInProgress = false;
let pendingUserDataQueue = [];
let lastBootstrapedUserId = undefined;
let lastBootstrapCallTime = 0;
let initializedLanguage = null;
const BOOTSTRAP_DEBOUNCE_MS = 100; // Debounce rapid calls within 100ms

const CHATLYN_NAMESPACE_KEY = '__chatlyn';
const LEGACY_COORDINATOR_KEY = '__productFruitsCoordinator';

const getChatlynWindow = () => window;

/**
 * Normalize language code to base language (e.g., "en-US" -> "en")
 * @param {string} language - Language code to normalize
 * @returns {string} Normalized language code
 */
const normalizeLanguage = (language) => {
  if (!language || typeof language !== 'string') {
    return 'en';
  }
  const [baseLanguage] = language.split('-');
  return baseLanguage ? baseLanguage.toLowerCase() : 'en';
};

const getNamespaceRoot = () => {
  const globalWindow = getChatlynWindow();
  const root = globalWindow[CHATLYN_NAMESPACE_KEY] || (globalWindow[CHATLYN_NAMESPACE_KEY] = {});
  if (!root.productFruits) {
    root.productFruits = {};
  }
  return root;
};

const ensureNamespace = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const root = getNamespaceRoot();
  const namespace = root.productFruits;

  if (
    namespace &&
    !Object.getOwnPropertyDescriptor(window, LEGACY_COORDINATOR_KEY)
  ) {
    Object.defineProperty(window, LEGACY_COORDINATOR_KEY, {
      configurable: true,
      get() {
        return namespace.coordinator;
      },
      set(value) {
        namespace.coordinator = value;
      },
    });
  }

  return namespace;
};

const setCoordinatorInstance = (coordinator) => {
  const namespace = ensureNamespace();
  if (!namespace) {
    return;
  }

  namespace.coordinator = coordinator;
};

const registerUnloadCleanup = () => {
  if (unloadHookRegistered || typeof window === 'undefined') {
    return;
  }

  unloadHookRegistered = true;
  window.addEventListener('beforeunload', () => {
    destroyProductFruitsCoordinator();
  });
};

// Re-export for backward compatibility
export { isProductFruitsEnabled };

export const destroyProductFruitsCoordinator = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const namespace = getChatlynWindow()[CHATLYN_NAMESPACE_KEY]?.productFruits;
  namespace?.coordinator?.destroy();
  if (namespace) {
    namespace.coordinator = undefined;
  }
  coordinatorPromise = null;
  initializedLanguage = null;
  
  const hostWindow = getChatlynWindow();
  if (hostWindow.productFruitsIsReady) {
    hostWindow.productFruitsIsReady = false;
  }
};

export const getOrCreateCoordinator = async () => {
  if (typeof window === 'undefined') {
    throw new Error('ProductFruits requires a browser environment');
  }

  const namespace = ensureNamespace();
  if (namespace?.coordinator) {
    return namespace.coordinator;
  }

  if (!coordinatorPromise) {
    coordinatorPromise = import('./coordinator').then(
      ({ ProductFruitsCoordinator: CoordinatorClass }) => {
        const instance = new CoordinatorClass();
        setCoordinatorInstance(instance);
        registerUnloadCleanup();
        return instance;
      }
    );

    if (namespace) {
      namespace.coordinatorPromise = coordinatorPromise;
    }
  }

  return coordinatorPromise;
};

const waitForProductFruitsServices = async (maxWaitMs = 3000) => {
  const startTime = Date.now();
  const hostWindow = getChatlynWindow();
  
  while (Date.now() - startTime < maxWaitMs) {
    if (typeof window === 'undefined') return null;
    
    const pf = hostWindow.productFruits;
    const services = pf && typeof pf === 'object' && 'services' in pf ? pf.services : undefined;
    
    if (
      services &&
      typeof services === 'object' &&
      'destroyAsync' in services &&
      typeof services.destroyAsync === 'function'
    ) {
      return services;
    }
    
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  
  return null;
};

const destroyProductFruitsInstance = async () => {
  if (typeof window === 'undefined') return;
  
  const hostWindow = getChatlynWindow();
  const services = await waitForProductFruitsServices();
  
  if (services) {
    try {
      await services.destroyAsync();
      // Give SDK time to clean up
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (err) {
      console.warn('[ProductFruits] destroyAsync failed:', err);
    }
  }
  
  hostWindow.productFruitsIsReady = false;
};

const initProductFruitsShell = async (user, localeOverride = null) => {
  const hostWindow = getChatlynWindow();
  const namespace = hostWindow[CHATLYN_NAMESPACE_KEY]?.productFruits;

  if (!namespace?.shell) {
    return;
  }

  const shell = namespace.shell;
  const currentLocale = localeOverride || shell.locale || 'en';
  const normalizedLocale = normalizeLanguage(currentLocale);

  // Check if we need to reinitialize due to language change
  const needsReinit = hostWindow.productFruitsIsReady && initializedLanguage !== normalizedLocale;

  if (needsReinit) {
    // Destroy existing Product Fruits instance before reinitializing
    await destroyProductFruitsInstance();
  }

  // Skip if already initialized with the same language
  if (hostWindow.productFruitsIsReady && initializedLanguage === normalizedLocale) {
    return;
  }

  performInit(hostWindow, namespace, user, normalizedLocale);
};

const performInit = (hostWindow, namespace, user, normalizedLocale) => {
  const shell = namespace.shell;
  const props = {
    ...(shell.payload.props || {}),
    userId: sanitizeUserId(user),
  };

  const payload = {
    ...shell.payload,
    props,
  };

  // Ensure $productFruits array exists (created by ProductFruits script loader)
  if (!hostWindow.$productFruits) {
    hostWindow.$productFruits = [];
  }

  if (Array.isArray(hostWindow.$productFruits)) {
    hostWindow.$productFruits.push(['init', shell.workspace, normalizedLocale, payload]);
    hostWindow.productFruitsIsReady = true;
    initializedLanguage = normalizedLocale;
    
    // Dispatch custom event for other components that might be waiting
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new Event('productfruits_ready'));
    }
  }
};

const sanitizeUserId = (user) => {
  if (!user || typeof user !== 'object') {
    return undefined;
  }

  // ProductFruitsUser has [key: string]: unknown, so we can safely check for 'id'
  const id = 'id' in user ? user.id : undefined;
  
  // Handle null/undefined explicitly
  if (id === null || id === undefined) {
    return undefined;
  }

  // Only allow primitive number or string types
  if (typeof id === 'number') {
    // Ensure it's a valid number (not NaN or Infinity)
    if (Number.isFinite(id)) {
      return id;
    }
    return undefined;
  }

  if (typeof id === 'string') {
    // Sanitize string: remove any potentially dangerous characters
    // Allow alphanumeric, hyphens, underscores, and dots
    const sanitized = id.replace(/[^a-zA-Z0-9._-]/g, '');
    return sanitized.length > 0 ? sanitized : undefined;
  }

  return undefined;
};

const performBootstrap = async (user) => {
  const coordinator = await getOrCreateCoordinator();
  coordinator.setUserData({
    ...user,
    preferences: user.preferences || {},
  });

  await initProductFruitsShell(user);
};

const processBootstrapQueue = async () => {
  if (bootstrapInProgress || pendingUserDataQueue.length === 0) {
    return;
  }

  bootstrapInProgress = true;

  try {
    while (pendingUserDataQueue.length > 0) {
      const nextUser = pendingUserDataQueue.shift();
      if (nextUser) {
        // eslint-disable-next-line no-await-in-loop
        await performBootstrap(nextUser);
      }
    }
  } finally {
    bootstrapInProgress = false;
    // Process any new items that arrived during processing
    if (pendingUserDataQueue.length > 0) {
      await processBootstrapQueue();
    }
  }
};

export const bootstrapProductFruitsForUser = async (user) => {
  if (typeof window === 'undefined' || !user || !isProductFruitsEnabled()) {
    return;
  }

  const now = Date.now();
  // Extract and validate user ID
  const userId = 
    typeof user.id === 'number' || typeof user.id === 'string' 
      ? user.id 
      : undefined;
  const namespace = getChatlynWindow()[CHATLYN_NAMESPACE_KEY]?.productFruits;
  const coordinator = namespace?.coordinator;
  
  // Debounce: If called multiple times rapidly with same user, only process once
  const isRapidCall = 
    now - lastBootstrapCallTime < BOOTSTRAP_DEBOUNCE_MS &&
    userId !== undefined &&
    userId === lastBootstrapedUserId;
  
  lastBootstrapCallTime = now;

  // Deduplication: If coordinator already exists and user ID hasn't changed,
  // and no bootstrap is in progress, just update user data without re-initializing
  if (
    coordinator &&
    userId !== undefined &&
    userId === lastBootstrapedUserId &&
    !bootstrapInProgress &&
    pendingUserDataQueue.length === 0
  ) {
    // User ID is the same and no bootstrap is in progress, just update data
    coordinator.setUserData({
      ...user,
      preferences: user.preferences || {},
    });
    return;
  }

  // Skip if this is a rapid duplicate call
  if (isRapidCall && bootstrapInProgress) {
    return;
  }

  // New user or bootstrap needed - add to queue
  lastBootstrapedUserId = userId;
  pendingUserDataQueue.push(user);
  await processBootstrapQueue();
};

/**
 * Update Product Fruits language when locale changes
 * @param {string} locale - New locale code
 * @param {object} user - Current user object
 */
export const updateProductFruitsLanguage = async (locale, user) => {
  if (typeof window === 'undefined' || !isProductFruitsEnabled() || !user) {
    return;
  }

  const normalizedLocale = normalizeLanguage(locale);
  
  // Only reinitialize if language actually changed
  if (initializedLanguage === normalizedLocale) {
    return;
  }

  const namespace = getChatlynWindow()[CHATLYN_NAMESPACE_KEY]?.productFruits;
  if (!namespace?.shell) {
    return;
  }

  // Reinitialize with new language
  await initProductFruitsShell(user, normalizedLocale);
};

export default {
  bootstrapProductFruitsForUser,
  destroyProductFruitsCoordinator,
  getOrCreateCoordinator,
  isProductFruitsEnabled,
  updateProductFruitsLanguage,
};

